import { Flex, ButtonV2, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import React, { useMemo, useState, useEffect } from 'react';
import { isEqual } from 'lodash-es';
import { FiltersPanelProps } from './FiltersPanel.types';
import { useTranslation } from '@shared/core';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { debounce } from 'lodash-es';
import { checkboxOverrides, FiltersPanelSkeleton, UserRatingFacet, StarRatingFacet, PriceRangeFacet, FacilitiesFacet } from './components';
import { Close } from '@withjoy/joykit/icons';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { PRICE_RANGE } from '../../constants';
import { GuestRating } from '@apps/accommodations/AccommodationsConsole.types';
import { useListingsPanelTelemetry } from '../ListingsPanel/ListingsPanel.telemetry';
import { SortOptionsFacet } from '../ListingsPanel/components/SortOptionsFacet';
import { useAccommodationsConsoleConfig } from '@apps/accommodations/AccommodationsConsoleConfigContext';
import { JoyTravelBenefitsFacet } from './components/JoyTravelBenefitsFacet';
import { ShowAvailableOnlyFacet } from './components/ShowAvailableOnlyFacet';
import { CompactMap } from './components/CompactMap';

const STAR_RATING_VALUES = [5, 4, 3, 2, 1];

function updateArrayValues<T>(source: T[], newValue: T) {
  return source.includes(newValue) ? source.filter(r => r !== newValue) : [...source, newValue];
}

const Container: React.FC<{}> = ({ children }) => {
  return (
    <Flex
      flexDirection="column"
      padding={6}
      width={{ _: 'full', md: pxToRem(292) }}
      maxWidth={{ _: 'full', md: pxToRem(292) }}
      height="100%"
      overflow="auto"
      rowGap={6}
    >
      {children}
    </Flex>
  );
};

const FILTERS_PANEL_ACTIONS_Z_INDEX = 2;

const FiltersPanel = ({
  prices,
  onClose,
  onReset,
  filters,
  eventName,
  facilityCounts,
  onFiltersChange,
  isLoadingPrices,
  starRatingCounts,
  guestRatingCounts,
  hidePriceRangeFacet,
  supportedFacilities,
  sortOption,
  onSortChange,
  mapData,
}: FiltersPanelProps) => {
  const { t } = useTranslation('accommodationsConsole');
  const filterPanelTrans = t('filterPanel');
  const isMobileOrTablet = useIsMobileOrTablet();
  const telemetry = useListingsPanelTelemetry();
  const config = useAccommodationsConsoleConfig();
  const [priceRange, setPriceRange] = useState<[number, number]>(filters.priceRange || [PRICE_RANGE.MIN, PRICE_RANGE.MAX]);
  const [debouncedPriceRange, setDebouncedPriceRange] = useState<[number, number]>(filters.priceRange || [PRICE_RANGE.MIN, PRICE_RANGE.MAX]);

  useEffect(() => {
    if (!isEqual(priceRange, filters.priceRange)) {
      setPriceRange(filters.priceRange);
      setDebouncedPriceRange(filters.priceRange);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.priceRange]);

  const guestRatingsOptions = useMemo(
    () => [
      { rating: 9, text: filterPanelTrans.guestRating.wonderful() },
      { rating: 8, text: filterPanelTrans.guestRating.veryGood() },
      { rating: 7, text: filterPanelTrans.guestRating.good() },
      { rating: 6, text: filterPanelTrans.guestRating.pleasant() },
    ],
    [filterPanelTrans],
  );

  const handleShowAvailableOnlyToggle = useEventCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const action = event.target.checked ? 'added' : 'removed';
    telemetry.showAvailableOnlyFilterChanged({ action });
    const newFilters = { ...filters, showAvailableOnly: event.target.checked };
    onFiltersChange(newFilters);
  });

  const handleJoyTravelBenefitsToggle = useEventCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const action = event.target.checked ? 'added' : 'removed';
    telemetry.joyTravelBenefitsFilterChanged({ action });
    const newFilters = { ...filters, cug: event.target.checked };
    onFiltersChange(newFilters);
  });

  const handleFacilityToggle = useEventCallback((facility: string) => {
    const isCurrentlySelected = filters.facilities?.includes(facility) || false;
    const action = isCurrentlySelected ? 'removed' : 'added';
    telemetry.facilityFilterChanged({ facility, action });
    const newFilters = { ...filters, facilities: updateArrayValues(filters.facilities || [], facility) };
    onFiltersChange(newFilters);
  });

  const handleStarRatingToggle = useEventCallback((rating: number): void => {
    const isCurrentlySelected = filters.starRating?.includes(rating) || false;
    const action = isCurrentlySelected ? 'removed' : 'added';
    telemetry.starRatingFilterChanged({ rating, action });
    const newFilters = { ...filters, starRating: updateArrayValues(filters.starRating || [], rating) };
    onFiltersChange(newFilters);
  });

  const handleUserRatingToggle = useEventCallback((rating: GuestRating): void => {
    const isCurrentlySelected = filters.userRating?.includes(rating) || false;
    const action = isCurrentlySelected ? 'removed' : 'added';
    telemetry.guestRatingFilterChanged({ rating, action });
    const newFilters = { ...filters, userRating: updateArrayValues(filters.userRating || [], rating) };
    onFiltersChange(newFilters);
  });

  const handlePriceRangeChange = useEventCallback((value: number | readonly number[]) => {
    const newRange: [number, number] = Array.isArray(value) ? [value[0], value[1]] : [value, value];
    setPriceRange(newRange);
  });

  useEffect(() => {
    const debouncedUpdate = debounce((value: [number, number]) => {
      setDebouncedPriceRange(value);
    }, 1000);

    debouncedUpdate(priceRange);
    return () => debouncedUpdate.cancel();
  }, [priceRange]);

  useEffect(() => {
    if (!isEqual(debouncedPriceRange, filters.priceRange)) {
      telemetry.priceRangeFilterChanged({ minPrice: debouncedPriceRange[0], maxPrice: debouncedPriceRange[1] });
    }
    const newFilters = { ...filters, priceRange: debouncedPriceRange };
    onFiltersChange(newFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedPriceRange]);

  const handleResetWithTelemetry = useEventCallback(() => {
    telemetry.allFiltersReset();
    onReset?.();
  });

  return (
    <>
      <Container>
        {mapData && !mapData.shouldShowFullMap && (
          <CompactMap
            location={mapData.location}
            onShowOnMap={mapData.onShowOnMap}
          />
        )}
        <Flex justifyContent={'space-between'}>
          {isMobileOrTablet || config.filtersPanel.hideEventNameAndNote ? (
            <TextV2 typographyVariant="hed4">{filterPanelTrans.filters()}</TextV2>
          ) : (
            <Flex
              flexDirection="column"
              gap={2}
            >
              <TextV2
                fontFamily="Forum"
                fontSize={pxToRem(32)}
                fontWeight={400}
              >
                {eventName}
              </TextV2>
              <TextV2 typographyVariant="label2">{filterPanelTrans.findHotels()}</TextV2>
            </Flex>
          )}

          {isMobileOrTablet && <Close onClick={onClose} />}
        </Flex>

        <ShowAvailableOnlyFacet
          title={filterPanelTrans.showAvailableOnly.title()}
          checkboxOverrides={checkboxOverrides}
          isChecked={filters.showAvailableOnly}
          onToggle={handleShowAvailableOnlyToggle}
        />

        {isMobileOrTablet && (
          <SortOptionsFacet
            selectedValue={sortOption}
            onChange={onSortChange}
          />
        )}

        {config.filtersPanel.shouldShowJoyTravelBenefitsFilter && (
          <JoyTravelBenefitsFacet
            title={filterPanelTrans.joyTravelBenefits.title()}
            checkboxOverrides={checkboxOverrides}
            isCUGChecked={filters.cug || false}
            onCUGToggle={handleJoyTravelBenefitsToggle}
          />
        )}

        <UserRatingFacet
          title={filterPanelTrans.guestRating.title()}
          guestRatingsOptions={guestRatingsOptions}
          selectedRatings={filters.userRating}
          guestRatingCounts={guestRatingCounts}
          onRatingToggle={handleUserRatingToggle}
          checkboxOverrides={checkboxOverrides}
        />

        {!hidePriceRangeFacet && (
          <PriceRangeFacet
            title={filterPanelTrans.priceRange.title()}
            description={filterPanelTrans.priceRange.description()}
            priceRange={priceRange}
            onPriceRangeChange={handlePriceRangeChange}
            isLoading={isLoadingPrices}
            prices={prices}
          />
        )}

        <StarRatingFacet
          title={filterPanelTrans.starRating.title()}
          description={filterPanelTrans.starRating.description()}
          starRatings={STAR_RATING_VALUES}
          selectedRatings={filters.starRating}
          starRatingCounts={starRatingCounts}
          onRatingToggle={handleStarRatingToggle}
          checkboxOverrides={checkboxOverrides}
        />

        <FacilitiesFacet
          title={filterPanelTrans.facilities.title()}
          facilities={supportedFacilities}
          selectedFacilities={filters.facilities || []}
          facilityCounts={facilityCounts}
          onFacilityToggle={handleFacilityToggle}
          checkboxOverrides={checkboxOverrides}
        />
      </Container>
      {isMobileOrTablet && (
        <Flex
          gap={4}
          marginTop={6}
          zIndex={FILTERS_PANEL_ACTIONS_Z_INDEX}
          width={'100%'}
          position="sticky"
          bottom={0}
          background="white"
          padding={4}
          borderTop="1px solid"
          borderTopColor="mono3"
        >
          <ButtonV2
            variant="ghost"
            intent="neutral"
            width="50%"
            onClick={handleResetWithTelemetry}
          >
            {filterPanelTrans.resetAllFilters()}
          </ButtonV2>
          <ButtonV2
            variant="solid"
            intent="neutral"
            shape="rounded"
            width="50%"
            onClick={() => {
              onClose?.();
            }}
          >
            {filterPanelTrans.showResults()}
          </ButtonV2>
        </Flex>
      )}
    </>
  );
};

FiltersPanel.Skeleton = () => {
  return (
    <Container>
      <FiltersPanelSkeleton />
    </Container>
  );
};

export { FiltersPanel };
